'use client';

// Hub motion primitives — Framer Motion wrappers.
// All primitives respect prefers-reduced-motion via useReducedMotion().
// Server Components must NOT import from here; use as client-boundary wrappers.

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

// ─── Shared Variants ─────────────────────────────────────────────────────────

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const cardVariantsReduced = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.15 } },
};

const gridVariants = {
  visible: { transition: { staggerChildren: 0.07 } },
};

// ─── HubCardGrid ─────────────────────────────────────────────────────────────
// Wraps a card grid and staggers the entrance of direct children.
// Apply this around the `.hub-card-grid` div.
//
// Usage:
//   <HubCardGrid className="hub-card-grid">
//     {items.map(i => <HubCardItem key={i.id}>...</HubCardItem>)}
//   </HubCardGrid>

export function HubCardGrid({ children, className, style }) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      className={className}
      style={style}
      variants={gridVariants}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.div>
  );
}

// ─── HubCardItem ─────────────────────────────────────────────────────────────
// A single animated card in a HubCardGrid. Renders as <article> by default.

export function HubCardItem({ children, className, style, as = 'article' }) {
  const reduced = useReducedMotion();
  const Tag = motion[as] || motion.article;

  return (
    <Tag
      className={className}
      style={style}
      variants={reduced ? cardVariantsReduced : cardVariants}
    >
      {children}
    </Tag>
  );
}

// ─── HubSectionReveal ────────────────────────────────────────────────────────
// Fades + slides a section up when it enters the viewport (once).
// Wraps a <section> element.

export function HubSectionReveal({ children, className, style }) {
  const reduced = useReducedMotion();

  return (
    <motion.section
      className={className}
      style={style}
      initial={{ opacity: 0, y: reduced ? 0 : 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: reduced ? 0.1 : 0.4, ease: 'easeOut' }}
    >
      {children}
    </motion.section>
  );
}

// ─── HubPageTransition ───────────────────────────────────────────────────────
// Wraps a page root for a subtle opacity+translate entrance on navigation.

export function HubPageTransition({ children, className, style }) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      className={className}
      style={style}
      initial={{ opacity: 0, y: reduced ? 0 : 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduced ? 0.1 : 0.28, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

// ─── HubAnimateList ──────────────────────────────────────────────────────────
// Wraps a list of items with AnimatePresence for filter/data change transitions.
// Children must have unique `key` props.

export function HubAnimateList({ children, className }) {
  return (
    <AnimatePresence mode="popLayout">
      <motion.div className={className}>{children}</motion.div>
    </AnimatePresence>
  );
}

// ─── HubFadeItem ─────────────────────────────────────────────────────────────
// Individual animated item for use inside HubAnimateList.

export function HubFadeItem({ children, itemKey, className }) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      key={itemKey}
      className={className}
      initial={{ opacity: 0, scale: reduced ? 1 : 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: reduced ? 1 : 0.97 }}
      transition={{ duration: reduced ? 0.1 : 0.22, ease: 'easeOut' }}
      layout
    >
      {children}
    </motion.div>
  );
}
